import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type BulkPatch, type ReorderMove } from '@/lib/api.ts';
import type {
  TaskFilter,
  TaskSort,
  CreateTaskInput,
  UpdateTaskInput,
  TaskWithRelations,
} from '@shared/types';

/**
 * Query keys are centralised so invalidation is consistent. Every task list
 * lives under ['tasks', …], which lets a single mutation refresh every view at
 * once — a completed task must vanish from Today AND update its project count.
 */
export const keys = {
  tasks: ['tasks'] as const,
  taskList: (filter: TaskFilter, sorts?: TaskSort[]) => ['tasks', 'list', filter, sorts] as const,
  task: (id: string) => ['tasks', 'detail', id] as const,
  subtasks: (id: string) => ['tasks', 'subtasks', id] as const,
  projects: ['projects'] as const,
  tags: ['tags'] as const,
  views: ['views'] as const,
};

export function useTasks(
  filter: TaskFilter = {},
  sorts?: TaskSort[],
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: keys.taskList(filter, sorts),
    queryFn: () => api.tasks.list(filter, sorts),
    // Lets callers hold a query back — the palette should not hit the server
    // until there is actually something to search for.
    enabled: options.enabled ?? true,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: keys.task(id ?? ''),
    queryFn: () => api.tasks.get(id!),
    enabled: id !== null,
  });
}

export function useSubtasks(id: string | null) {
  return useQuery({
    queryKey: keys.subtasks(id ?? ''),
    queryFn: () => api.tasks.subtasks(id!),
    enabled: id !== null,
  });
}

export function useProjects() {
  return useQuery({ queryKey: keys.projects, queryFn: () => api.projects.list() });
}

export function useTags() {
  return useQuery({ queryKey: keys.tags, queryFn: () => api.tags.list() });
}

/**
 * Refreshes everything a task change can touch. Tasks carry project and tag
 * rollups, so a task write invalidates those lists too rather than leaving a
 * stale count in the sidebar.
 */
function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: keys.tasks });
    void queryClient.invalidateQueries({ queryKey: keys.projects });
    void queryClient.invalidateQueries({ queryKey: keys.tags });
  };
}

export function useCreateTask() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => api.tasks.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateTask() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateTaskInput & { id: string }) =>
      api.tasks.update(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteTask() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api.tasks.remove(id),
    onSuccess: invalidate,
  });
}

export function useBulkUpdate() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: ({ ids, patch }: { ids: string[]; patch: BulkPatch }) =>
      api.tasks.bulkUpdate(ids, patch),
    onSuccess: invalidate,
  });
}

export function useBulkDelete() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: (ids: string[]) => api.tasks.bulkDelete(ids),
    onSuccess: invalidate,
  });
}

/**
 * Applies a drag, updating the cache before the request completes.
 *
 * Without this the card visually snaps back to where it started and then jumps
 * to its new home once the round trip finishes — over Funnel from a phone that
 * is a very visible stutter. On failure the snapshot is restored, so a dropped
 * connection puts the card back rather than leaving the board lying.
 */
export function useReorderTasks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (moves: ReorderMove[]) => api.tasks.reorder(moves),

    onMutate: async (moves) => {
      await queryClient.cancelQueries({ queryKey: keys.tasks });
      const snapshot = queryClient.getQueriesData({ queryKey: keys.tasks });

      const byId = new Map(moves.map((m) => [m.id, m]));

      queryClient.setQueriesData<TaskWithRelations[]>({ queryKey: keys.tasks }, (old) => {
        if (!Array.isArray(old)) return old;
        return old.map((task) => {
          const move = byId.get(task.id);
          if (!move) return task;
          return {
            ...task,
            position: move.position,
            ...(move.status !== undefined ? { status: move.status } : {}),
            ...(move.project_id !== undefined ? { project_id: move.project_id } : {}),
          };
        });
      });

      return { snapshot };
    },

    onError: (_err, _moves, context) => {
      for (const [key, data] of context?.snapshot ?? []) {
        queryClient.setQueryData(key, data);
      }
    },

    // Reconcile with the server either way — the board groups by status, and a
    // rejected move must not leave a card sitting in the wrong column.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: keys.tasks });
      void queryClient.invalidateQueries({ queryKey: keys.projects });
    },
  });
}

// --- saved views ------------------------------------------------------------

export function useViews() {
  return useQuery({ queryKey: keys.views, queryFn: () => api.views.list() });
}

export function useView(id: string | null) {
  return useQuery({
    queryKey: [...keys.views, id] as const,
    queryFn: () => api.views.get(id!),
    enabled: id !== null,
  });
}

function useInvalidateViews() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: keys.views });
}

export function useCreateView() {
  const invalidate = useInvalidateViews();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.views.create>[0]) => api.views.create(input),
    onSuccess: invalidate,
  });
}

export function useUpdateView() {
  const invalidate = useInvalidateViews();
  return useMutation({
    mutationFn: ({ id, ...input }: Parameters<typeof api.views.update>[1] & { id: string }) =>
      api.views.update(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteView() {
  const invalidate = useInvalidateViews();
  return useMutation({
    mutationFn: (id: string) => api.views.remove(id),
    onSuccess: invalidate,
  });
}
